/**
 * Document-specific upload dialog components (Refactored)
 * Clean, maintainable components following code quality guidelines
 */

'use client';

import { useDocumentUpload } from '@/hooks/use-document-upload';
import { BaseUploadDialog } from './base-upload-dialog';
import type { CoreDocumentUploadHandler } from './core-document-upload-handler';
import { DIALOG_CONFIGS, getAcceptedFileTypes } from './dialog-configs';

interface SpecificUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: CoreDocumentUploadHandler;
  maxFiles?: number;
}

/**
 * Generic Document Upload Dialog Component
 * Reduces code duplication by using configuration-driven approach
 */
function DocumentUploadDialog({
  configKey,
  open,
  onOpenChange,
  onUpload,
  maxFiles
}: {
  configKey: keyof typeof DIALOG_CONFIGS;
} & SpecificUploadDialogProps) {
  const config = DIALOG_CONFIGS[configKey];
  const uploadConfig = {
    ...config.upload,
    ...(maxFiles && { maxFiles })
  };

  const {
    files,
    hasFiles,
    isUploading,
    canUpload,
    removeFile,
    updateFileType,
    updateCertifiedCopy,
    handleFilesSelected,
    handleUpload,
    handleClose,
    openFileDialog,
    getInputProps
  } = useDocumentUpload(uploadConfig, {
    onUpload,
    onClose: () => onOpenChange(false)
  });

  return (
    <BaseUploadDialog
      open={open}
      onOpenChange={onOpenChange}
      content={config.content}
      files={files}
      hasFiles={hasFiles}
      isUploading={isUploading}
      canUpload={canUpload}
      onUpload={handleUpload}
      onClose={handleClose}
      onFilesSelected={handleFilesSelected}
      onRemoveFile={removeFile}
      onFileTypeChange={updateFileType}
      onCertifiedCopyChange={updateCertifiedCopy}
      openFileDialog={openFileDialog}
      getInputProps={getInputProps}
      acceptedFileTypes={getAcceptedFileTypes(config)}
      hideFileTypeSelector={true}
      onlyCrucial={config.filePreview?.onlyCrucial}
      excludeCrucial={config.filePreview?.excludeCrucial}
    />
  );
}

// Individual dialog components - now much smaller and focused
export function DeathCertificateUploadDialog(props: SpecificUploadDialogProps) {
  return <DocumentUploadDialog configKey="deathCertificate" {...props} />;
}

export function WillUploadDialog(props: SpecificUploadDialogProps) {
  return <DocumentUploadDialog configKey="will" {...props} />;
}

export function GrantOfRepresentationUploadDialog(
  props: SpecificUploadDialogProps
) {
  return <DocumentUploadDialog configKey="grantOfRepresentation" {...props} />;
}

export function PrimaryIdUploadDialog(props: SpecificUploadDialogProps) {
  return <DocumentUploadDialog configKey="primaryId" {...props} />;
}

export function SecondaryIdUploadDialog(props: SpecificUploadDialogProps) {
  return <DocumentUploadDialog configKey="secondaryId" {...props} />;
}

export function LetterheadUploadDialog(props: SpecificUploadDialogProps) {
  return <DocumentUploadDialog configKey="letterhead" {...props} />;
}

export function AdditionalDocumentUploadDialog(
  props: SpecificUploadDialogProps
) {
  return <DocumentUploadDialog configKey="additionalDocument" {...props} />;
}
